import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { Bot, CheckCircle2, Inbox, XCircle } from "lucide-react";
import { useApi, useAuth, useHandoff, useInvalidateOn, useRealtimeEvent, type AiProposal, type PagedResult } from "@ncp/api-client";
import { formatDateTime, useEnumLabel, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, EmptyState, Field, NativeSelect, PageHeader, Skeleton, StatCard, toast, InfoHint } from "@ncp/ui";
import { AgentRunPanel, ProposalList, useResolveDeepLinks } from "@ncp/assistant-sdk";

interface InboxSummary {
  pending: number;
  approved: number;
  edited: number;
  rejected: number;
  avgEditDistance: number;
  byAgent: Array<{ agent: string; count: number }>;
  estimatedHoursSaved: number;
}

/** An approval made within the minimum review period the Department set, or of a draft its approver never opened (Condition 3.10). */
interface QuickApproval { id: string; caseId?: string | null; caseNumber?: string | null; kind: string; title: string; titleAr?: string | null; reviewerName?: string | null; openedAt?: string | null; approvedAt: string; reviewSeconds?: number | null }
interface QuickApprovals { minimumSeconds: number; items: QuickApproval[] }
/** The head of section and those above see the report of quick approvals. */
const QUICK_READERS = ["HeadOfComplaints", "Director", "AssistantUndersecretary", "Undersecretary", "Admin"];

/** The AI Inbox. Embedded in the Command Center, it drops its own heading and the summary cards (those figures are on the Statistics page). */
export function InboxPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const status = params.get("status") ?? "Proposed";
  const agentKey = params.get("agent") ?? "";
  // Registry deep links: `?proposal=` shows that proposal whatever its status, `?run=` opens its agent activity.
  const focus = params.get("proposal");
  const [runId, setRunId] = useState<string | null>(params.get("run"));
  const handoff = useHandoff();
  const resolve = useResolveDeepLinks();

  const { user } = useAuth();
  const quickReader = !!user?.roles.some((r) => QUICK_READERS.includes(r));
  const quickView = status === "quick" && quickReader;
  const quick = useQuery({ queryKey: ["proposals", "quick-approvals"], queryFn: () => api.get<QuickApprovals>("/api/ai/proposals/quick-approvals"), enabled: quickView });
  const proposals = useQuery({
    enabled: !quickView,
    queryKey: ["proposals", status, agentKey, focus],
    queryFn: async () => focus
      ? { items: [await api.get<AiProposal>(`/api/ai/proposals/${focus}`)], total: 1, page: 1, pageSize: 1 } satisfies PagedResult<AiProposal>
      : api.get<PagedResult<AiProposal>>("/api/ai/proposals", { status: status || undefined, agentKey: agentKey || undefined, pageSize: 50 }),
  });
  // Playbook matters are worked in the AI Workspace; the registry says where.
  const openCase = (id: string, p: AiProposal) => {
    if (p.caseType !== "Generic") { navigate(`/cases/${id}`); return; }
    handoff.open(resolve({ scope: "case", caseId: id }).then((d) => d.links.find((l) => l.solutionKey !== "backoffice")?.url ?? null, () => null));
  };
  const summary = useQuery({ queryKey: ["proposals-summary"], queryFn: () => api.get<InboxSummary>("/api/ai/proposals/summary") });
  const agents = useQuery({ queryKey: ["agents"], queryFn: () => api.get<Array<{ key: string; name: string; nameAr: string; tier: string }>>("/api/ai/agents") });

  useInvalidateOn(["proposal.created", "proposal.updated"], [["proposals"], ["proposals-summary"]]);
  useRealtimeEvent<{ title: string; agentKey: string; caseId?: string }>("proposal.created", (p) => toast.info(t("ai.proposal"), { description: p.title }));


  const items = proposals.data?.items ?? [];

  return (
    <div>
      {embedded ? null : <PageHeader title={<span className="inline-flex items-center gap-2">{t("backoffice:inbox.title")}<InfoHint>{t("backoffice:inbox.subtitle")}</InfoHint></span>} />}

      <div className={embedded ? "hidden" : "mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3"}>
        <StatCard label={t("ai.pendingReview")} value={summary.data?.pending ?? 0} icon={Inbox} />
        <StatCard label={t("backoffice:commandCenter.proposalsApproved")} value={(summary.data?.approved ?? 0) + (summary.data?.edited ?? 0)} icon={CheckCircle2} tone="sea" footer={`${summary.data?.edited ?? 0} ${t("backoffice:commandCenter.proposalsEdited").toLowerCase()}`} />
        <StatCard label={t("backoffice:commandCenter.proposalsRejected")} value={summary.data?.rejected ?? 0} icon={XCircle} tone="camel" />
      </div>

      {focus ? (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-aegold-200 bg-aegold-50/60 px-4 py-2.5 text-sm">
          <span>{t("backoffice:inbox.focused")}</span>
          <Button size="sm" variant="outline" onClick={() => setParams((p) => { p.delete("proposal"); return p; })}>{t("backoffice:inbox.showAll")}</Button>
        </div>
      ) : (
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t("misc.status")} className="w-44">
          {(id) => (
            <NativeSelect id={id} value={status} onChange={(e) => setParams((p) => { p.set("status", e.target.value); return p; })}>
              {["Proposed", "Approved", "Edited", "Rejected", ""].map((s) => <option key={s} value={s}>{s === "" ? t("misc.all") : t(`ai.${s.toLowerCase()}`, { defaultValue: s })}</option>)}
              {quickReader ? <option value="quick">{t("backoffice:r3Process.inbox.quick")}</option> : null}
            </NativeSelect>
          )}
        </Field>
        <Field label={t("backoffice:inbox.filterAgent")} className="w-64">
          {(id) => (
            <NativeSelect id={id} value={agentKey} onChange={(e) => setParams((p) => { e.target.value ? p.set("agent", e.target.value) : p.delete("agent"); return p; })}>
              <option value="">{t("misc.all")}</option>
              {agents.data?.filter((a) => a.tier !== "n/a").map((a) => <option key={a.key} value={a.key}>{locale === "ar" ? a.nameAr : a.name}</option>)}
            </NativeSelect>
          )}
        </Field>
        {/* The count is the count of the cards listed below (visual items 10 and 24). */}
        {quickView ? <span className="mb-2 inline-flex items-center gap-1 text-sm text-sand-800">{t("backoffice:r3Process.inbox.quickCount", { count: quick.data?.items.length ?? 0 })}<InfoHint>{t("backoffice:r3Process.inbox.quickHint", { count: quick.data?.minimumSeconds ?? 120 })}</InfoHint></span>
          : <Badge variant="ai" className="mb-2"><Bot /> {t("backoffice:r3Process.inbox.count", { count: items.length })}</Badge>}
      </div>
      )}

      {quickView ? <QuickApprovalsList data={quick.data} loading={quick.isLoading} onOpen={(q) => navigate(q.caseId ? `/cases/${q.caseId}` : `/inbox?proposal=${q.id}`)} /> : proposals.isLoading ? (
        <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>
      ) : proposals.isError && focus ? (
        <EmptyState icon={Inbox} title={t("backoffice:inbox.focusMissing")} />
      ) : items.length === 0 ? (
        <EmptyState icon={Inbox} title={t("backoffice:inbox.empty")} />
      ) : (
        <div className="grid gap-4 xl:grid-cols-[1fr_380px]">
          <ProposalList proposals={items} invalidate={[["proposals"], ["proposals-summary"], ["cases"]]} onOpenRun={setRunId} onOpenCase={openCase} focusId={focus} />
          <div className="hidden xl:block">
            <div className="sticky top-24">
              {runId ? <AgentRunPanel runId={runId} onClose={() => setRunId(null)} /> : (
                <EmptyState icon={Bot} title={t("ai.agentActivity")} description={t("backoffice:inbox.activityHint")} className="py-10" />
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

/** Approvals made sooner than the minimum review period after the approver opened the draft, or without opening it: for the head of section. */
function QuickApprovalsList({ data, loading, onOpen }: { data?: QuickApprovals; loading: boolean; onOpen: (approval: QuickApproval) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const enumLabel = useEnumLabel();
  if (loading) return <Skeleton className="h-40 rounded-2xl" />;
  const items = data?.items ?? [];
  if (items.length === 0) return <EmptyState icon={CheckCircle2} title={t("backoffice:r3Process.inbox.quickNone")} />;
  return (
    <ul className="divide-y divide-sand-100 rounded-xl border border-sand-200 bg-white">
      {items.map((q) => (
        <li key={q.id}>
          <button type="button" onClick={() => onOpen(q)} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-start text-sm hover:bg-sand-50">
            <span className="font-mono text-xs text-aegold-800" dir="ltr">{q.caseNumber ?? t("backoffice:r3Process.inbox.withoutFile")}</span>
            <span className="min-w-0 flex-1 truncate">{locale === "ar" && q.titleAr ? q.titleAr : q.title}</span>
            <Badge size="sm" variant="outline">{enumLabel("proposalKind", q.kind)}</Badge>
            <span className="text-xs text-muted-foreground">{q.reviewerName}</span>
            <span className="text-xs text-warning-800">{q.openedAt && q.reviewSeconds != null ? t("backoffice:r3Process.inbox.reviewTime", { count: q.reviewSeconds }) : t("backoffice:r3Process.inbox.notOpened")}</span>
            <span className="text-xs text-muted-foreground">{formatDateTime(q.approvedAt, locale)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
